import { Link } from 'react-router-dom';
import type { HomepageConfig } from '@cng/shared';
import { SmartImage } from '@/components/common/SmartImage';

/**
 * The service-pill marquee. Like the announcement ticker, the item list is
 * duplicated so `translateX(-50%)` loops without a visible seam.
 */
export function ServicePills({ config }: { config: HomepageConfig['servicePills'] }) {
  const items = (config?.items ?? []).filter((i) => i.enabled).sort((a, b) => a.order - b.order);
  if (!config?.enabled || items.length === 0) return null;

  const rendered = [...items, ...items];

  return (
    <div className="salon-slider-wrapper" role="region" aria-label="Our services">
      <div
        className="salon-track"
        style={{ animationDuration: `${config.speedSeconds ?? 40}s` }}
      >
        {rendered.map((pill, index) => {
          const duplicate = index >= items.length;
          const body = (
            <>
              <div className="service-icon">
                <SmartImage
                  media={pill.image}
                  alt={duplicate ? '' : (pill.image?.alt || pill.title)}
                  sizes="56px"
                />
              </div>
              <div className="service-text">
                <span className="service-title">{pill.title}</span>
                {pill.subtitle ? <span className="service-sub">{pill.subtitle}</span> : null}
              </div>
            </>
          );

          const key = `${pill.title}-${index}`;
          if (!pill.href) {
            return <div className="service-pill" key={key} aria-hidden={duplicate || undefined}>{body}</div>;
          }
          const internal = pill.href.startsWith('/') && !pill.href.startsWith('//');
          return internal ? (
            <Link
              className="service-pill"
              to={pill.href}
              key={key}
              aria-hidden={duplicate || undefined}
              tabIndex={duplicate ? -1 : undefined}
            >
              {body}
            </Link>
          ) : (
            <a
              className="service-pill"
              href={pill.href}
              key={key}
              aria-hidden={duplicate || undefined}
              tabIndex={duplicate ? -1 : undefined}
            >
              {body}
            </a>
          );
        })}
      </div>
    </div>
  );
}
